<template>
  <div :class="$b()">
    <div :class="$b('title-reviews-container')">
      <ProductTitleSection
        :customised-product="customisedProduct"
        :product-name="productName"
        :frame-type="
          customisedProduct.frameTypes.length > 1
            ? customisedProduct.frameType ||
              customisedProduct.product.frameTypeOverride
            : null
        "
        :subtitle="productSubtitle"
        :price="price"
        :pre-discount-price="preDiscountPrice"
        :is-minimum-price="customisedProduct.showMinimumPrice"
        :subtitle-above-title="false"
        :show-prescription-title="prescriptionMode"
        :show-reviews="(showReviews && !useGenericReviews) || !isCasesPageMode"
        :review-summary="reviewSummary"
        @launch-reviews="launchReviews"
      />

      <StockThresholdIndicator
        v-if="showStockThresholdIndicator"
        :customised-product="customisedProduct"
        :class="$b('stock-threshold-indicator')"
      />
    </div>

    <ProductCustomiserBlock
      v-if="!isCasesPageMode"
      ref="customiserBlock"
      :variants="swatchSelectVariants"
      :active-swatch-index="activeSwatchIndex"
      :limited-edition-count="limitedEditionHeroDesignsLength"
      :customised-product="customisedProduct"
      :active-tab-index="activeCustomiserTabIndex"
      :show-iris-preview="showIrisPreview"
      :page-mode="pageMode"
      :customiser-mode="customiserMode"
      :is-composite-pdp="isCompositePdp"
      :composite-variants="compositeVariants"
      :icons-follow-lens="iconsFollowLens"
      :logos-follow-lens="logosFollowLens"
      :bottom-follow-frame="bottomFollowFrame"
      :frame-follow-strap="frameFollowStrap"
      :review-summary="reviewSummary"
      :custom-button-active="customButtonActive"
      :prescription-mode="prescriptionMode"
      @show-reviews="launchReviews"
      @update:active-tab-index="handleCustomiserTabChange"
      @active-swatch-index-changed="handleHeroDesignClick"
      @customiser-mode-changed="$emit('update:customiserMode', $event)"
      @switched-le-to-custom="handleLimitedEditionToCustom"
      @select="(choice) => handleSelect(choice)"
      @show-lens-guide="$emit('show-lens-guide')"
      @iris-preview-state-changed="
        (state) => $emit('update:showIrisPreview', state)
      "
      @toggle-bottom-frame-link="$emit('toggle-bottom-frame-link')"
      @toggle-icon-lens-link="$emit('toggle-icon-lens-link')"
      @toggle-frame-strap-link="$emit('toggle-frame-strap-link')"
      @unlink-icons-and-lenses="$emit('unlink-icons-and-lenses')"
      @close-customiser="closeCustomiser()"
      @prescription-mode-toggled="$emit('prescription-mode-toggled', $event)"
    />

    <CaseCustomiser
      v-if="
        isCasesPageMode &&
        !['case-ct_capsule', 'case-ct_pacecapsule'].includes(
          customisedProduct.sku
        )
      "
      :customised-product="customisedProduct"
    />

    <div
      v-if="showLensTechSelect"
      id="lens-tech-select-tabs"
      :class="$b('lens-tech-select-tabs')"
    >
      <LensTechSelectTabs
        :customised-product="customisedProduct"
        :prescription-mode="prescriptionMode"
        :show-missing-lens-tech-msg="showMissingLensTechMsg"
        :selected-product-type="selectedProductType"
        @show-lens-guide="$emit('show-lens-guide')"
        @toggled-product-type="$emit('toggled-product-type', $event)"
        @change="
          (option) => {
            $emit('change-lens-tech', {
              sku: option.lensSku,
              productOptionId: option.productOptionId || null,
              lensTechCode: option.tech,
            })

            showMissingLensTechMsg = false
          }
        "
        @select-prebuilt="handlePrebuiltVariantLensTechSelect"
      />

      <div
        v-if="showPrescriptionToggle"
        :class="$b('prescription-toggle')"
      >
        <VisionLink
          :is-button="true"
          :line-link="true"
          :mono-font="true"
          theme="grey"
          :class="$b('prescription-toggle-text')"
          @click.prevent="handlePrescriptionToggleClick()"
        >
          <template v-if="!prescriptionMode"> Prescription Lenses </template>
          <template v-else> Non-Prescription Lenses </template>
        </VisionLink>
        <VisionLink
          v-if="prescriptionMode"
          :is-button="true"
          :line-link="true"
          :mono-font="true"
          theme="grey"
          :class="$b('prescription-toggle-text')"
          @click="handleHowItWorksClick"
        >
          How Prescription works
        </VisionLink>
        <VisionLink
          v-if="showLensGuideButton"
          :is-button="true"
          :line-link="true"
          theme="grey"
          :mono-font="true"
          :class="$b('lens-guide')"
          @click="$emit('show-lens-guide')"
        >
          Lens Guide
        </VisionLink>
      </div>
    </div>

    <FrameTypeSelectTabs
      v-if="showFrameTypeSelect"
      :customised-product="customisedProduct"
      :hide-conversion-kit="customisedProduct.product.pageMode !== 'custom'"
      @toggle-frame-selector-modal-visible="frameSelectModalVisible = $event"
    />

    <FrameTypeSelectModal
      v-if="frameSelectModalVisible && showFrameTypeSelect"
      :modal-visible="frameSelectModalVisible"
      :customised-product="customisedProduct"
      :hide-conversion-kit="customisedProduct.product.pageMode !== 'custom'"
      :price="price"
      @toggle-frame-selector-modal-visible="frameSelectModalVisible = $event"
    />

    <RegisterInterestForm
      v-if="showRegisterInterestForm"
      :class="$b('register-interest-form')"
      :customised-product="customisedProduct"
    />

    <CharityDonationTooltip
      v-if="customisedProduct.hasButtonToolTip"
      :customised-product="customisedProduct"
    />

    <NotificationTooltip
      v-if="customisedProduct.isPrescription"
      title="Single Vision Prescription"
      content="We do not currently offer varifocal lenses or readers. Prescription SunGods are for distance or near vision only."
    />

    <div v-if="!soldOut" :class="$b('sticky-add-to-bag', { visible: stickyAddToBagVisible })">
      <div :class="$b('sticky-add-to-bag-wrapper')">
        <button
          :class="$b('add-to-bag-swatch')"
          @click="handleSwatchClick"
        >
          <DynamicImg
            v-if="customisedProduct.legacySKU"
            :sku="customisedProduct.legacySKU"
            :width="100"
            :height="100"
            bg="transparent"
            :view-angle="2"
          />
        </button>
        <AddToBag
          v-if="!showRegisterInterestForm"
          :price="price"
          :customised-product="customisedProduct"
          :pre-discount-price="preDiscountPrice"
          :active-part-type="activePartType"
          :requires-lens-tech-selection="showLensTechSelect"
          tracking-location="pdp - sticky button"
          :is-sticky-button="true"
          @lens-tech-not-selected="handleLensTechNotSelected"
          @toggle-promo-selector="$emit('toggle-promo-selector', $event)"
          @set-primary-sku-added-to-cart="
            $emit('set-primary-sku-added-to-cart', $event)
          "
        />
      </div>
    </div>

    <div :class="$b('add-to-bag')">
      <AddToBag
        v-if="!showRegisterInterestForm"
        :price="price"
        :customised-product="customisedProduct"
        :pre-discount-price="preDiscountPrice"
        :active-part-type="activePartType"
        :requires-lens-tech-selection="showLensTechSelect"
        tracking-location="pdp"
        @lens-tech-not-selected="showMissingLensTechMsg = $event"
        @toggle-promo-selector="$emit('toggle-promo-selector', $event)"
        @set-primary-sku-added-to-cart="
          $emit('set-primary-sku-added-to-cart', $event)
        "
        @scrolled-past-view="handleAddToBagInView"
      />
    </div>

    <VisionButton
      v-if="isCompositePdp"
      :to="customisedProduct.product.baseProduct.url"
      :class="$b('see-more-button')"
      background-colour="white"
      size="small"
    >
      See More {{ customisedProduct.product.baseProduct.name }}
    </VisionButton>

    <VisionButton
      v-if="secondaryCtaLink && !isCompositePdp"
      :to="secondaryCtaLink"
      :class="$b('see-more-button')"
      background-colour="white"
      :square="true"
      size="small"
    >
      <template #content>
        <!-- eslint-disable vue/no-v-html -->
        <span v-html="$tr(parseText(secondaryCtaLinkText))" />
        <!-- eslint-enable vue/no-v-html -->
      </template>
    </VisionButton>

    <div
      v-if="shippingCountry === 'US' || locale === 'en-us'"
      :class="$b('tariff-note')"
    >
      Orders shipped to the US may be subject to extra taxes and duties, due to recent changes in import tariffs.
    </div>

    <div :class="$b('value-proposition')">
      <ValueProposition
        :show-register-interest-form="showRegisterInterestForm"
        :customised-product="customisedProduct"
      />
    </div>
  </div>
</template>

<script>
import { storeToRefs } from 'pinia'
import CustomisedProduct from '~/models/CustomisedProduct'
import parseTextMixin from '~/mixins/parseTextMixin'

const orderedSku = (unorderedSku) => {
  const skuParts = (unorderedSku || '').split('-')
  const baseSKU = skuParts[0]
  return [baseSKU, ...skuParts.slice(1).sort()].join('-')
}

export default {
  name: 'ProductInfoPanel',

  mixins: [parseTextMixin],

  props: {
    customisedProduct: { type: CustomisedProduct, required: true },
    customiserMode: { type: Boolean, default: false },
    conversionKitEnabled: { type: Boolean, default: false },
    showIrisPreview: { type: Boolean, default: false },
    reviewSummary: { type: Object, required: true },
    cartUpdating: { type: Boolean, default: false },
    cartUpdated: { type: Boolean, default: false },
    isCompositePdp: { type: Boolean, default: false },
    compositeProducts: { type: Array, default: () => [] },
    preDiscountPrice: { type: Number, required: true },
    price: { type: [Number, null], required: true },
    skusForReviews: { type: [Array, String], default: () => [] },
    productIds: { type: Array, default: () => [] },
    titleOverride: { type: String, default: '' },
    reviewSidebarOpen: { type: Boolean, default: false },
    iconsFollowLens: { type: Boolean, default: false },
    logosFollowLens: { type: Boolean, default: false },
    bottomFollowFrame: { type: Boolean, default: false },
    frameFollowStrap: { type: Boolean, default: false },
    showLensTechSelect: { type: Boolean, default: false },
    prescriptionMode: { type: Boolean, default: false },
    skuOnLoad: { type: String, default: '' },
    activePartType: { type: String, default: '' },
    showStockThresholdIndicator: { type: Boolean, default: false },
    hasLifetimeGuarantee: { type: Boolean, default: true },
    selectedProductType: { type: String, default: 'sunglasses' },
    swatchSelectVariants: { type: Array, default: () => [] },
    compositeVariants: { type: Array, default: () => [] }
  },

  emits: [
    'update:customiserMode',
    'change-customiser-tab',
    'change-lens-tech',
    'show-lens-guide',
    'update:showIrisPreview',
    'choice-selected',
    'dialog-open-state-changed',
    'failed-over-lens-tech',
    'show-reviews',
    'show-prescription-how-it-works-modal',
    'toggle-bottom-frame-link',
    'toggle-icon-lens-link',
    'toggle-frame-strap-link',
    'unlink-icons-and-lenses',
    'prescription-mode-toggled',
    'add-to-cart',
    'toggled-product-type',
    'toggle-promo-selector',
    'set-primary-sku-added-to-cart'
  ],

  setup() {
    const store = useStorefrontStore()
    const { shippingCountryName, shippingCountry } = storeToRefs(store)

    const { resolveLocalePath, buildInternalLink } = useUrlBuilder()

    const { $i18n } = useNuxtApp()
    const locale = computed(() => {
      return $i18n.locale
    })

    return {
      shippingCountryName,
      shippingCountry,
      resolveLocalePath,
      buildInternalLink,
      locale
    }
  },

  data: () => ({
    activeCustomiserTabIndex: 0,
    activeCustomiserTab: '',
    showCountryDropdown: false,
    showDeliveryMessage: false,
    deliveryMessage: '',
    dialogOpen: false,
    previousSelectedSwatchIndex: null,
    customButtonActive: false,
    showMissingLensTechMsg: false,
    frameSelectModalVisible: false,
    stickyAddToBagVisible: false,
  }),

  computed: {
    lePart() {
      return Object.values(this.customisedProduct.choices).find(
        (option) => option.isLimitedEdition
      )
    },
    pageMode() {
      return this.customisedProduct.product.pageMode
    },
    limitedEditionHeroDesigns() {
      return this.customisedProduct.heroDesigns.filter(
        (item) => item.isLimitedEdition === true
      )
    },
    limitedEditionHeroDesignsLength() {
      return this.limitedEditionHeroDesigns.length
    },
    showLensGuideButton() {
      return !this.customisedProduct.isLimitedEdition && !this.prescriptionMode
    },
    activeSwatchIndex() {
      if (this.isCompositePdp)
        return this.compositeVariants.findIndex(this.isDesignSelected)

      let swatchIndex = this.findSwatchIndex()

      // If no index found and not limited edition, use previously selected index as fallback only when custom button hasn't been set to active
      // Fixes issue when switching between Pace Top and Full/Top & Full frame sometimes can't match either SKU or normalisedSKU because frame colours different
      if (
        !this.customButtonActive &&
        swatchIndex === -1 &&
        !this.customisedProduct.isLimitedEdition
      ) {
        swatchIndex = this.previousSelectedSwatchIndex
      }

      return swatchIndex
    },
    showFrameTypeSelect() {
      if (
        (this.customisedProduct.isLimitedEdition &&
          this.customisedProduct.product.frameTypeOverride) ||
        (this.customisedProduct.product.swappableParts.length &&
          ['custom', 'conversionkit'].includes(
            this.customisedProduct.product.pageMode
          ))
      )
        return true
      return false
    },
    productName() {
      return this.customisedProduct.productName
    },
    productSubtitle() {
      if (this.customisedProduct.isLimitedEdition) return null
      return this.customisedProduct.subTitle
    },
    baseSKU() {
      return this.customisedProduct.baseSKU
    },
    sku() {
      return this.customisedProduct.sku
    },
    secondaryCtaLink() {
      return this.buildInternalLink(
        this.customisedProduct.product.pdpConfig?.secondaryCtaLink
      )
    },
    secondaryCtaLinkText() {
      return this.customisedProduct.product.pdpConfig?.secondaryCtaLinkText
    },
    isLRK() {
      return this.pageMode === 'lenses'
    },
    addToCartCopyOverride() {
      if (!this.showLensTechSelect) return null // No lens tech selection available

      if (this.customisedProduct.isLimitedEdition) return null // Limited edition product

      return 'Select Lens and Buy'
    },
    useGenericReviews() {
      return this.customisedProduct?.product?.useGenericReviews
    },
    isCasesPageMode() {
      return this.customisedProduct?.product?.pageMode === 'cases'
    },
    showReviews() {
      return this.customisedProduct?.product?.showReviews
    },
    showRegisterInterestForm() {
      if (!this.customisedProduct.product.pdpConfig.showRegisterInterestForm)
        return false // Disabled in Dato
      if (this.customisedProduct.isInStock) return false // In stock
      if (this.customisedProduct.preorderPartSelected) return false // On preorder
      return true
    },
    showFilterToggle() {
      return (
        this.customisedProduct.isPrescriptionSunglassesAvailable &&
        this.customisedProduct.product.pageMode === 'custom'
      )
    },
    delayedDeliveryUnix() {
      if (this.prescriptionMode) {
        const now = this.$dayjs().add(18, 'day')
        return now.valueOf() / 1000
      }

      return this.customisedProduct.delayedDespatchDate
    },
    showPrescriptionToggle() {
      return (
        this.showLensTechSelect &&
        this.customisedProduct.isPrescriptionSunglassesAvailable &&
        this.customisedProduct.product.pageMode !== 'lenses'
      )
    },
    soldOut() {
      return (
        !this.customisedProduct.preorderPartSelected &&
        !this.customisedProduct.isInStock
      )
    }
  },

  watch: {
    '$route.path'() {
      this.activeCustomiserTabIndex = 0
    },
    activeCustomiserTabIndex() {
      this.$emit('change-customiser-tab', this.activeCustomiserTab)
    }
  },

  created() {
    if (
      !['parts', 'conversionkit'].includes(
        this.customisedProduct.product.pageMode
      )
    )
      return
    const activePart = Object.keys(this.customisedProduct.choices)[0]
    const activeIndex = this.customisedProduct.visibleParts.findIndex(
      (part) => part.stockManagedSKUPrefix === activePart
    )
    this.activeCustomiserTabIndex = Math.max(0, activeIndex)
  },

  mounted() {
    // Emit this so that we know the active customiser tab immediately
    this.$emit('change-customiser-tab', this.activeCustomiserTab)
  },

  methods: {
    isDesignSelected(design) {
      if (design.isLimitedEditionPart) {
        return (
          this.customisedProduct.sku.includes(design.limitedEditionPartSKU) ||
          this.customisedProduct.legacySKU.includes(
            design.limitedEditionPartSKU
          )
        )
      }

      const normalisedSKU = this.customisedProduct.normalisedSKU
      return design.normalisedSKU === normalisedSKU
    },
    findSwatchIndex() {
      const { sku, skuWithoutLensTech, isPrebuiltVariant } = this.customisedProduct

      let swatchIndex = (this.swatchSelectVariants || []).findIndex(
        (variant) => {
          if (orderedSku(variant.sku) !== orderedSku(sku)) return false // SKU doesn't match
          if (variant.isPrebuilt && !isPrebuiltVariant) return false // Prebuilt variant not selected
          if (!variant.isPrebuilt && isPrebuiltVariant) return false // Prebuilt variant selected
          return true
        }
      )

      // If no swatch found, then check using skuWithoutLensTech
      if (swatchIndex === -1) {
        swatchIndex = (this.swatchSelectVariants || []).findIndex(
          (variant) => {
            if (orderedSku(variant.skuWithoutLensTech) !== orderedSku(skuWithoutLensTech)) return false // SKU doesn't match
            if (variant.isPrebuilt && !isPrebuiltVariant) return false // Prebuilt variant not selected
            if (!variant.isPrebuilt && isPrebuiltVariant) return false // Prebuilt variant selected
            return true
          }
        )
      }

      this.customButtonActive = swatchIndex === -1

      return swatchIndex
    },
    handleSelect(choice) {
      this.customisedProduct.selectPrebuiltVariant(null)

      if (typeof choice === 'string') {
        this.$emit('choice-selected', choice)
        return
      }
      this.$emit('choice-selected', choice.mainPart.sku)
      if (choice.followerPart.sku)
        this.$emit('choice-selected', choice.followerPart.sku)
    },
    handleCustomiserTabChange({ idx, part }) {
      this.activeCustomiserTabIndex = idx
      this.activeCustomiserTab = part
      this.$emit('change-customiser-tab', part)
    },
    handleHeroDesignClick(designIndex, toCustom = null) {
      this.customisedProduct.setProductCustomisedState(false)

      this.previousSelectedSwatchIndex = designIndex

      const design = (
        this.isCompositePdp ? this.compositeVariants : this.swatchSelectVariants
      )[designIndex]
      if (!design) return

      this.$track.product.selectHeroDesign(
        this.customisedProduct,
        design,
        designIndex
      )

      if (design.isLimitedEdition && !design.isPartBasedLimitedEdition) {
        return navigateTo({
          path: this.resolveLocalePath(design.url),
          query: {
            scrollTop: false,
          },
        })
      } else if (this.customisedProduct.isLimitedEdition) {
        return navigateTo({
          path: this.resolveLocalePath(design.url),
          query: {
            scrollTop: false,
            customiser: toCustom ? '1' : null,
            sku: design.sku,
          },
        })
      } else if (design.isPrebuilt) {
        this.customisedProduct.selectPrebuiltVariant(design.variant)
      } else {
        const { hasFailedOverLensTech } =
          this.customisedProduct.setChoicesFromSKU(design.sku)
        if (hasFailedOverLensTech) this.$emit('failed-over-lens-tech')
      }
    },
    handleLimitedEditionToCustom() {
      this.handleHeroDesignClick(0, true)
    },
    handlePrebuiltVariantLensTechSelect (variant) {
      this.customisedProduct.selectPrebuiltVariant(variant)
      this.customisedProduct.setLensTechSelectedState(true)
      this.showMissingLensTechMsg = false
    },
    showLensTechModal(showContinueShopping = false) {
      this.$refs.lensTechSelect?.showModal(showContinueShopping)
    },
    skuExistsInSwatchVariants() {
      return !!this.swatchSelectVariants.filter((swatchItem) => {
        return swatchItem.normalisedSKU === this.customisedProduct.normalisedSKU
      }).length
    },
    closeCustomiser() {
      this.customButtonActive = this.findSwatchIndex() === -1
    },
    launchReviews() {
      this.$emit('dialog-open-state-changed', true)
      this.$emit('show-reviews')
    },
    handleHowItWorksClick() {
      this.$emit('show-prescription-how-it-works-modal')
      this.$track.global.linkClicked({
        interactionElement: 'Prescription How It Works',
        interactionSection: 'PDP Product Info Panel',
        interactionCategory: 'Open Modal',
      })
    },
    async handlePrescriptionToggleClick() {
      this.$emit('prescription-mode-toggled', !this.prescriptionMode)

      await this.$track.global.linkClicked(
        {
          interactionElement: 'prescription-toggle',
          interactionSection: 'Lens Tech',
        },
        'Prescription Mode Toggle Clicked'
      )
    },
    handleAddToBagInView(inView) {
      this.stickyAddToBagVisible = inView
    },
    handleSwatchClick() {
      this.$scrollTo('#product-info', {
        offset: -300,
        duration: 300,
      })
    },
    handleLensTechNotSelected(event) {
      this.showMissingLensTechMsg = event

      this.$scrollTo('#lens-tech-select-tabs', {
        offset: -300,
        duration: 300,
      })
    },
  },
}
</script>

<style lang="postcss">
.ProductInfoPanel {
  @apply md:sticky md:-top-20;
}

.ProductInfoPanel__title-reviews-container {
  @apply hidden;

  @screen md {
    @apply block;
  }
}

.ProductInfoPanel__customiser {
  background: theme('colors.white');
  margin-top: 0;
  transition: opacity 0.5s ease-in-out, margin-top 0.5s ease-in-out,
    max-height 0.5s ease-in-out;
  z-index: 2;
  overflow: hidden;

  @screen mdMax {
    opacity: 0;

    &--expanded {
      opacity: 1;
    }
  }

  @screen md {
    max-height: 0;
    margin-top: 0 !important;
    /* I'm sorry. */
  }
}

.ProductInfoPanel__sticky-add-to-bag {
  @apply fixed bottom-2 sm:right-0 left-0 sm:left-auto z-auto
    translate-y-[calc(100%+0.5rem)] transition-transform duration-300 ease-in-out
    sm:max-w-96
    mx-2;

  width: calc(100% - 0.5rem * 2);
}

.ProductInfoPanel__sticky-add-to-bag-wrapper {
  @apply flex justify-center gap-1 items-stretch
    max-w-96 smMax:mx-auto
    py-1 px-1
    backdrop-filter backdrop-blur
    rounded-lg overflow-hidden;
}

.ProductInfoPanel__add-to-bag-swatch {
  @apply aspect-square bg-white w-[16%] flex-shrink-0 flex-grow
    rounded-lg;
}

.ProductInfoPanel__sticky-add-to-bag--visible {
  @apply translate-y-0;
}

.ProductInfoPanel__see-more-button {
  @apply mt-xs w-full;
}

.ProductInfoPanel__shipping-notification-message {
  @apply mt-2;
}

.ProductInfoPanel__ratings-button {
  @apply inline-block cursor-pointer;
}

.ProductInfoPanel__prescription-how-it-works {
  @apply mt-6 text-center;
}

.ProductInfoPanel__lens-tech-select-tabs {
  @apply mt-6 mb-10;
}

.ProductInfoPanel__prescription-toggle {
  @apply mt-3 mb-8 md:text-xs flex flex-wrap gap-y-2 justify-between;
}

.ProductInfoPanel__lens-guide,
.ProductInfoPanel__prescription-toggle-text {
  @apply text-tiny;
}

.ProductInfoPanel__stock-threshold-indicator {
  @apply mt-4;
}

.ProductInfoPanel__value-proposition {
  @apply mt-6 mb-8;
}

.ProductInfoPanel__tariff-note {
  @apply my-6 text-xs text-grey-500;
}
</style>
